import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';

/**
 * Textarea with @mention autocomplete. Suggests active users as you type `@`.
 * Inserts `@<email-prefix>` on selection.
 *
 * Props:
 *  - value, onChange: controlled textarea
 *  - placeholder, rows, className, disabled, dataTestid
 */
export function MentionTextarea({
    value = '',
    onChange,
    placeholder,
    rows = 3,
    className = '',
    disabled = false,
    dataTestid,
    onSubmit,
}) {
    const textareaRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [queryState, setQueryState] = useState({ query: '', start: -1 });
    const [loading, setLoading] = useState(false);

    // Detect `@word` token right before the caret
    const detectMentionToken = useCallback((text, caretPos) => {
        // Scan backwards from caret to find '@'
        let i = caretPos - 1;
        while (i >= 0) {
            const c = text[i];
            if (c === '@') {
                const prev = i > 0 ? text[i - 1] : ' ';
                // Must be preceded by whitespace or start-of-text
                if (!/\s/.test(prev) && prev !== '' && i !== 0) return null;
                const token = text.slice(i + 1, caretPos);
                if (!/^[A-Za-z0-9._-]*$/.test(token)) return null;
                return { start: i, query: token };
            }
            if (/\s/.test(c)) return null;
            i--;
        }
        return null;
    }, []);

    // Debounced API search
    useEffect(() => {
        if (!showDropdown) return;
        const handle = setTimeout(async () => {
            try {
                setLoading(true);
                const res = await apiClient.get(`/users/mention-search?q=${encodeURIComponent(queryState.query)}`);
                setSuggestions(res.data || []);
                setActiveIdx(0);
            } catch {
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 120);
        return () => clearTimeout(handle);
    }, [queryState.query, showDropdown]);

    const handleInput = (e) => {
        const newVal = e.target.value;
        onChange?.(newVal);
        const ta = textareaRef.current;
        const pos = ta?.selectionStart ?? newVal.length;
        const tok = detectMentionToken(newVal, pos);
        if (tok) {
            setQueryState(tok);
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
        }
    };

    const insertMention = (user) => {
        if (!user || !textareaRef.current) return;
        const ta = textareaRef.current;
        const caret = ta.selectionStart;
        const before = value.slice(0, queryState.start);
        const after = value.slice(caret);
        const handle = (user.handle || user.full_name || '').replace(/\s+/g, '-').toLowerCase();
        const inserted = `@${handle} `;
        const newVal = before + inserted + after;
        onChange?.(newVal);
        setShowDropdown(false);
        // Restore caret after the inserted mention
        setTimeout(() => {
            const newPos = before.length + inserted.length;
            ta.focus();
            ta.setSelectionRange(newPos, newPos);
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (showDropdown && suggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIdx((i) => (i + 1) % suggestions.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertMention(suggestions[activeIdx]);
                return;
            }
            if (e.key === 'Escape') {
                setShowDropdown(false);
                return;
            }
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && typeof onSubmit === 'function') {
            e.preventDefault();
            onSubmit();
        }
    };

    const ROLE_LABELS = {
        cs_agent: 'CS', cs_head: 'CS Head', mentor: 'Mentor', master_of_academics: 'Mentor',
        business_development: 'BD', bd_executive: 'BD', business_development_manager: 'BD Mgr',
        super_admin: 'Admin', admin: 'Admin', sales_executive: 'Sales', team_leader: 'TL',
    };

    return (
        <div className="relative w-full">
            <Textarea
                ref={textareaRef}
                value={value}
                onChange={handleInput}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                rows={rows}
                disabled={disabled}
                className={className}
                data-testid={dataTestid}
            />
            {showDropdown && (
                <div
                    className="absolute z-50 left-0 right-0 mt-1 max-h-52 overflow-y-auto rounded-md border bg-popover shadow-lg text-sm"
                    data-testid="mention-dropdown"
                >
                    {loading && suggestions.length === 0 ? (
                        <div className="flex items-center justify-center py-3 text-muted-foreground gap-2">
                            <Loader2 className="h-3 w-3 animate-spin" /> Searching users…
                        </div>
                    ) : suggestions.length === 0 ? (
                        <p className="py-3 text-center text-xs text-muted-foreground">No matching users</p>
                    ) : (
                        suggestions.map((u, i) => (
                            <button
                                type="button"
                                key={u.id}
                                onMouseEnter={() => setActiveIdx(i)}
                                onClick={() => insertMention(u)}
                                className={`w-full text-left flex items-center gap-2 px-3 py-2 ${i === activeIdx ? 'bg-accent' : 'hover:bg-accent/60'}`}
                                data-testid={`mention-option-${u.handle}`}
                            >
                                <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-semibold">
                                    {(u.full_name || u.handle || '?').slice(0, 1).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{u.full_name || u.handle}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">@{u.handle}</div>
                                </div>
                                <span className="text-[10px] px-1.5 py-0 rounded border bg-background text-muted-foreground">
                                    {ROLE_LABELS[u.role] || u.role}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

/** Render a note body, highlighting @mentions with pink pill styling. */
export function RenderNoteBody({ text }) {
    if (!text) return null;
    // Split while keeping mentions. Regex captures @token including dashes, dots, underscores.
    const parts = [];
    const regex = /@([A-Za-z][A-Za-z0-9._-]{1,50})/g;
    let lastIdx = 0;
    let m;
    while ((m = regex.exec(text)) !== null) {
        if (m.index > lastIdx) {
            parts.push({ type: 'text', content: text.slice(lastIdx, m.index) });
        }
        parts.push({ type: 'mention', content: m[0] });
        lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) {
        parts.push({ type: 'text', content: text.slice(lastIdx) });
    }
    return (
        <p className="whitespace-pre-wrap">
            {parts.map((p, i) =>
                p.type === 'mention' ? (
                    <span
                        key={i}
                        className="inline-block px-1.5 py-0 rounded bg-pink-500/15 text-pink-700 dark:text-pink-300 font-medium"
                    >
                        {p.content}
                    </span>
                ) : (
                    <React.Fragment key={i}>{p.content}</React.Fragment>
                )
            )}
        </p>
    );
}
